// API hook for the Artifacts tab (#11523). Plain useState/useEffect + fpFetch
// to match the Agents hooks.

import { useCallback, useEffect, useState } from "react";
import fpFetch from "../../../services/fpFetch";
import { BASE_API_URL_V2 } from "../../../constants";
import type { ChatCitationSource } from "../Chat/types";
import { toConversationSlug } from "../Chat/chatUtils";
import type { TableCell, TableColumn } from "../../FileViewer/tableArtifact";

export type ArtifactKind =
  | "report"
  | "document"
  | "file"
  | "diagram"
  // A typed rows-and-columns table (#11727); content holds its JSON.
  | "table";
export type ArtifactSourceKind = "user" | "chat" | "agent_run";

export interface ArtifactItem {
  id: string;
  /** Whether the caller may assign this artifact to records (r64):
   *  creator, or admitted by the producing agent. Optional so older
   *  payloads/fixtures fall back to created_by_me. */
  can_assign?: boolean;
  /** Whether the caller may edit the content, rename it and file it
   *  (#13422, #14320): the creator, an Editor of the agent that produced it,
   *  or an Editor grantee of it or of its folder. Delete and Share stay with
   *  `created_by_me`. Optional so older payloads/fixtures fall back to
   *  created_by_me. */
  can_edit?: boolean;
  // A chat creation not yet saved to the library (#11548 r75).
  draft?: boolean;
  title: string;
  description: string | null;
  kind: ArtifactKind;
  filename: string | null;
  workspace_id: string | null;
  content: string | null;
  source_kind: ArtifactSourceKind;
  source_conversation_id: string | null;
  source_agent_id: string | null;
  source_run_id: string | null;
  // Fan-out subject this artifact was produced for ("Brevan Howard Fund I");
  // written by arity child runs when that engine lands (#11548 round 9).
  subject_label: string | null;
  created_by_me: boolean;
  // The folder this artifact is filed in, when the reader can see that
  // folder (#14320); null = the root list.
  folder_id: string | null;
  // RMS records this artifact is assigned to (#11727 slice 3).
  entities: ArtifactEntity[];
  // The sources the CURRENT version cites (#11975): bracketed marker ->
  // [entry], the reply's own shape, and the turn scope a click resolves
  // against. Only a single-artifact read carries them; listings omit both.
  source_map?: Record<string, ChatCitationSource[]> | null;
  citation_scope?: string | null;
  // The rows of this table that name the record the listing was read for
  // (#12430), one entry per sheet that has any. Only a record's listing
  // carries them, and only for a table whose record column names it.
  mentions?: ArtifactMention[];
  created_at: string;
  updated_at: string;
}

/** One sheet's rows naming a record: the sheet's columns, the rows carried
 *  (capped by the server) and how many the table holds in all. */
export interface ArtifactMention {
  sheet: string;
  columns: TableColumn[];
  rows: Record<string, TableCell>[];
  // Aligned with `rows` (#14585): each row's cell sources, column key ->
  // markers the item's `source_map` resolves; null where the row cites
  // nothing. The server always sends it; optional only so the mention
  // fixtures in other story files need not carry it.
  sources?: (Record<string, string[]> | null)[];
  row_count: number;
}

/** A record an artifact is assigned to: the record's public id, kind and
 *  name — what the UI links and labels. */
export interface ArtifactEntity {
  id: string;
  kind: string;
  name: string;
}

export interface ArtifactFolder {
  id: string;
  name: string;
  /** Per viewer (#14320): whether they created it (delete and sharing are
   *  the creator's), may edit it (rename it, file into and out of it), and
   *  whether it carries any grant. */
  created_by_me: boolean;
  can_edit: boolean;
  is_shared: boolean;
  created_at: string;
  updated_at: string;
}

const apiPath = (path: string) => `${BASE_API_URL_V2}/api/v2${path}`;

/** Where the viewer reads a produced-file artifact from, and where it
 *  downloads it from. Same split as chat outputs (#10988): preview serves a
 *  renderable form, download serves the original bytes. */
export const artifactUrls = (
  artifactId: string
): { previewUrl: string; downloadUrl: string } => ({
  previewUrl: apiPath(`/artifacts/${artifactId}/preview`),
  downloadUrl: apiPath(`/artifacts/${artifactId}/download`),
});

/** The error a refused request carries: the server's own sentence (the
 *  `detail` every HttpError answers with), else the status line. A refusal
 *  the page cannot foresee (a folder the artifact's creator cannot see, the
 *  organization gate, #14320) then reads as the server's reason. */
async function refusal(response: Response): Promise<Error> {
  // The network stand-in fpFetch hands back has no body methods.
  if (typeof response.json === "function") {
    try {
      const detail = ((await response.json()) as { detail?: unknown }).detail;
      if (typeof detail === "string" && detail) return new Error(detail);
    } catch {
      // Not JSON: fall back to the status line.
    }
  }
  return new Error(`${response.status}: ${response.statusText}`);
}

/** The folders the caller can see (#11523, #14320): their own and the
 *  ones shared with them. */
export async function fetchArtifactFolders(): Promise<ArtifactFolder[]> {
  const response = await fpFetch(apiPath("/artifacts/folders"), {
    "x-finpilot-auth": true,
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  return ((await response.json()) as { items: ArtifactFolder[] }).items;
}

/** Create a folder; a duplicate name for this user is a 409. */
export async function createArtifactFolder(
  name: string
): Promise<ArtifactFolder> {
  const response = await fpFetch(apiPath("/artifacts/folders"), {
    "x-finpilot-auth": true,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) {
    throw await refusal(response);
  }
  return (await response.json()) as ArtifactFolder;
}

/** Rename a folder (creator-only; duplicate names 409). */
export async function renameArtifactFolder(
  folderId: string,
  name: string
): Promise<ArtifactFolder> {
  const response = await fpFetch(apiPath(`/artifacts/folders/${folderId}`), {
    "x-finpilot-auth": true,
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) {
    throw await refusal(response);
  }
  return (await response.json()) as ArtifactFolder;
}

/** Delete a folder — its artifacts return to the root list. */
export async function deleteArtifactFolder(folderId: string): Promise<void> {
  const response = await fpFetch(apiPath(`/artifacts/folders/${folderId}`), {
    "x-finpilot-auth": true,
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw await refusal(response);
  }
}

/** File an artifact into a folder, or back to the root with null. */
export async function moveArtifactToFolder(
  artifactId: string,
  folderId: string | null
): Promise<void> {
  const response = await fpFetch(apiPath(`/artifacts/${artifactId}`), {
    "x-finpilot-auth": true,
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ folder_id: folderId }),
  });
  if (!response.ok) {
    throw await refusal(response);
  }
}

/** Rename an artifact (metadata-only; the listing order is untouched). */
export async function renameArtifact(
  artifactId: string,
  title: string
): Promise<void> {
  const response = await fpFetch(apiPath(`/artifacts/${artifactId}`), {
    "x-finpilot-auth": true,
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  });
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
}

export interface ArtifactVersion {
  version_number: number;
  source_kind: "chat" | "agent_run" | "hand_edit" | "restore";
  // Inline snapshot for text kinds; null for binary snapshots (served by
  // the version preview endpoint instead).
  content: string | null;
  has_file: boolean;
  // The snapshot's own filename: across a type conversion (docx → pdf)
  // this differs from the artifact's current filename, which is what makes
  // the history legible. Null for inline-content versions.
  filename: string | null;
  // The conversation/run that produced this version (#11548 round 9).
  source_conversation_id: string | null;
  source_run_id: string | null;
  // Origin (#11819): the chat TURN that cut a chat version, and the
  // AGENT whose run cut a run version. Null on hand edits and restores.
  source_turn_id: string | null;
  source_agent_id: string | null;
  // What this version's content cites and the scope it resolves against
  // (#11975), so a past version renders its citations too.
  source_map?: Record<string, ChatCitationSource[]> | null;
  citation_scope?: string | null;
  created_at: string;
  updated_at: string;
}

/** One artifact by id — the shape `openArtifact` needs (#11548 r41). */
export async function fetchArtifactItem(
  artifactId: string
): Promise<ArtifactItem> {
  const response = await fpFetch(apiPath(`/artifacts/${artifactId}`), {
    "x-finpilot-auth": true,
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  return (await response.json()) as ArtifactItem;
}

/** The artifact's version history, newest first (#11523). */
export async function fetchArtifactVersions(
  artifactId: string
): Promise<ArtifactVersion[]> {
  const response = await fpFetch(apiPath(`/artifacts/${artifactId}/versions`), {
    "x-finpilot-auth": true,
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  return ((await response.json()) as { items: ArtifactVersion[] }).items;
}

/** Where the viewer renders a binary version snapshot from. */
export const artifactVersionPreviewUrl = (
  artifactId: string,
  versionNumber: number
): string =>
  apiPath(`/artifacts/${artifactId}/versions/${versionNumber}/preview`);

/** Make a past version the artifact's current content; the restore itself is
 *  recorded as a new version. Returns the refreshed artifact. */
export async function restoreArtifactVersion(
  artifactId: string,
  versionNumber: number
): Promise<ArtifactItem> {
  const response = await fpFetch(
    apiPath(`/artifacts/${artifactId}/versions/${versionNumber}/restore`),
    {
      "x-finpilot-auth": true,
      method: "POST",
      headers: { "Content-Type": "application/json" },
    }
  );
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  return (await response.json()) as ArtifactItem;
}

/** Persist a hand-edit to an artifact's content (#11523): the new Mermaid
 *  source for a diagram, the new text of a markdown file, the JSON of a
 *  table, or base64 bytes for a binary rewrite. Creator-only server-side.
 *  Resolves to the refreshed artifact (a table comes back normalized); a
 *  rejection surfaces the server's own message — for a table that is the
 *  named validation problem the user has to fix (#11727). */
export async function saveArtifactContent(
  artifactId: string,
  body: { text: string } | { base64: string }
): Promise<ArtifactItem> {
  const response = await fpFetch(apiPath(`/artifacts/${artifactId}/content`), {
    "x-finpilot-auth": true,
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw await refusal(response);
  }
  return (await response.json()) as ArtifactItem;
}

/** The records an artifact is assigned to (#11727 slice 3). */
export async function fetchArtifactEntities(
  artifactId: string
): Promise<ArtifactEntity[]> {
  const response = await fpFetch(apiPath(`/artifacts/${artifactId}/entities`), {
    "x-finpilot-auth": true,
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  return ((await response.json()) as { items: ArtifactEntity[] }).items;
}

/** File the artifact under a record (creator-only; idempotent). Resolves to
 *  the artifact's full assignment list. */
export async function assignArtifactEntity(
  artifactId: string,
  entityId: string
): Promise<ArtifactEntity[]> {
  const response = await fpFetch(apiPath(`/artifacts/${artifactId}/entities`), {
    "x-finpilot-auth": true,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ entity_id: entityId }),
  });
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  return ((await response.json()) as { items: ArtifactEntity[] }).items;
}

/** Take the artifact off a record (creator-only). */
export async function unassignArtifactEntity(
  artifactId: string,
  entityId: string
): Promise<ArtifactEntity[]> {
  const response = await fpFetch(
    apiPath(`/artifacts/${artifactId}/entities/${entityId}`),
    {
      "x-finpilot-auth": true,
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    }
  );
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  return ((await response.json()) as { items: ArtifactEntity[] }).items;
}

/** The artifacts assigned to a record — visible to every org member. */
export async function fetchArtifactsForEntity(
  entityId: string,
  signal?: AbortSignal
): Promise<ArtifactItem[]> {
  // No `x-finpilot-auth` fail-fast here, as for the RMS reads the record
  // page makes beside it. The session header still goes out; a missing one
  // answers 401 instead of throwing before the request is made.
  const response = await fpFetch(
    apiPath(`/artifacts?entity_id=${encodeURIComponent(entityId)}`),
    { headers: { "Content-Type": "application/json" }, signal }
  );
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  return ((await response.json()) as { items: ArtifactItem[] }).items;
}

/** The artifacts a RUN produced a version of (#11548 r65) — through
 *  version provenance server-side, so an early run keeps its living doc
 *  after later runs versioned it. */
export async function fetchArtifactsForRun(
  runId: string
): Promise<ArtifactItem[]> {
  const response = await fpFetch(
    apiPath(`/artifacts?run_id=${encodeURIComponent(runId)}`),
    { headers: { "Content-Type": "application/json" } }
  );
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  return ((await response.json()) as { items: ArtifactItem[] }).items;
}

/** Soft-delete an artifact (creator-only server-side). Resolves on success;
 *  throws with the response status otherwise. */
export async function deleteArtifact(artifactId: string): Promise<void> {
  const response = await fpFetch(apiPath(`/artifacts/${artifactId}`), {
    "x-finpilot-auth": true,
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
}

export function useArtifacts(): {
  artifacts: ArtifactItem[];
  folders: ArtifactFolder[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
} {
  const [artifacts, setArtifacts] = useState<ArtifactItem[]>([]);
  const [folders, setFolders] = useState<ArtifactFolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // `loading` is the first read only. A re-read (after a link, rename or
  // delete) keeps the rows on screen until the new ones land: blanking the
  // list for it made the page blink (Gerry, 2026-10-13).
  const refresh = useCallback(async () => {
    setError(null);
    try {
      const response = await fpFetch(apiPath("/artifacts"), {
        "x-finpilot-auth": true,
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        throw new Error(`${response.status}: ${response.statusText}`);
      }
      const data = (await response.json()) as { items: ArtifactItem[] };
      setArtifacts(data.items);
      setFolders(await fetchArtifactFolders());
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { artifacts, folders, loading, error, refresh };
}

/** Where further prompts revise this artifact — the conversation that
 *  made it (r45): a chat conversation's own URL, an agent run's
 *  conversation URL, or null when nothing conversational made it (a user
 *  upload), which falls back to a new chat (r60, #13261: shared by the
 *  library's Chat menu item and every pane's Chat). */
export const editDestinationFor = (artifact: ArtifactItem): string | null => {
  if (artifact.source_kind === "chat" && artifact.source_conversation_id) {
    return `/chat/${toConversationSlug(artifact.source_conversation_id)}`;
  }
  if (
    artifact.source_kind === "agent_run" &&
    artifact.source_agent_id &&
    artifact.source_run_id &&
    artifact.source_conversation_id
  ) {
    return (
      `/agents/${artifact.source_agent_id}/runs/${artifact.source_run_id}` +
      `/conversations/${artifact.source_conversation_id}`
    );
  }
  return null;
};

/** Whether the caller may assign the artifact to records (r64): the
 *  server-computed capability when present, the creator fallback when a
 *  fixture or older payload lacks it. */
export const canAssignArtifact = (artifact: ArtifactItem): boolean =>
  artifact.can_assign ?? artifact.created_by_me;

/** Whether the caller may hand-edit, restore and rename the artifact
 *  (#13422): the server-computed capability when present, the creator
 *  fallback when a fixture or older payload lacks it. */
export const canEditArtifact = (artifact: ArtifactItem): boolean =>
  artifact.can_edit ?? artifact.created_by_me;

/** Save a chat-created draft to the library (#11548 r75). */
export async function saveArtifact(artifactId: string): Promise<ArtifactItem> {
  const response = await fpFetch(
    apiPath(`/artifacts/${encodeURIComponent(artifactId)}/save`),
    {
      method: "POST",
      "x-finpilot-auth": true,
      headers: { "Content-Type": "application/json" },
    }
  );
  if (!response.ok) {
    throw new Error(`${response.status}: ${response.statusText}`);
  }
  return (await response.json()) as ArtifactItem;
}

/** Where an artifact's Chat goes (r43/r45, #13261): the conversation that
 *  made it, with the artifact reopening beside it, or a new chat with the
 *  artifact beside an empty composer when nothing conversational did (or
 *  the lookup failed). Nothing is planted in the composer (Hunter: "a new
 *  chat with no preloaded text in the chat input"). The destination is
 *  tagged agentic explicitly: artifacts are agentic-chat products, the org
 *  flag alone would pick legacy for an org with agentic chat off, and the
 *  not-found probe only ever falls back agentic -> legacy — an untagged
 *  link could never open the source chat. */
export const artifactChatTarget = (
  artifact: ArtifactItem | null,
  artifactId: string
): { to: string; state: Record<string, unknown> } => ({
  to: (artifact ? editDestinationFor(artifact) : null) ?? "/chat",
  state: { openArtifactId: artifactId, chatBackendKind: "agentic" },
});
